/**
 * @Module: ErrorBoundary
 * @Status: Ready
 * @Summary: Catches render errors and shows a recovery UI instead of white screen
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, VStack, Heading, Text, Button } from '@chakra-ui/react';
import { AppIcon } from '../icons/AppIcon';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box bg="surface.page" minH="40vh" py={12} px={4}>
          <VStack spacing={4} maxW="400px" mx="auto" textAlign="center">
            <AppIcon name="ui.warning" size={40} color="var(--chakra-colors-terracotta-400)" />
            <Heading size="md" fontFamily="heading" color="text.primary">
              Something went wrong
            </Heading>
            <Text fontSize="sm" color="text.secondary">
              {this.props.fallbackMessage || "This section ran into an error. Your data is safe."}
            </Text>
            {this.state.error && (
              <Text fontSize="xs" color="text.muted" fontFamily="mono" noOfLines={2}>
                {this.state.error.message}
              </Text>
            )}
            <Button variant="kitchen-outline" size="sm" onClick={this.handleReset}>
              Try Again
            </Button>
            <Button variant="ghost" size="sm" color="text.muted"
              onClick={() => window.location.href = '/browse'}>
              Go to Browse
            </Button>
          </VStack>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
